import _, {
  isNaN,
  isFinite,
  capitalize,
  camelCase,
  isString,
  omit,
} from 'lodash';
import React from 'react';
import moment, { Duration } from 'moment';
import * as Sentry from '@sentry/react';
import {
  ICalibration,
  ISpace,
  IUsage,
  LatLngMultiPolygon,
  LatLngPolygon,
  LatLngRing,
  SpaceTypeCategoryMap,
} from 'spw-redux/modules/floorplan/types';
import { LatLng } from 'leaflet';
import * as R from 'ramda';
import { UAParser } from 'ua-parser-js';
import { hexToCSSFilter } from 'hex-to-css-filter';
import { ISpaceBreakDown } from 'spw-redux/modules/program-summary/types';
import { isNil } from 'ramda';
import {
  CUSTOM_CATEGORY as OTHER_CATEGORY,
  DEFAULT_COLORS,
  INDIVIDUAL_WORKSPACES_CATEGORY,
  NO_DATA_TO_SHOW,
  CUSTOM_WORKSPACE_COLORS,
  CUSTOM_MEETING_SPACE_COLORS,
  TOTAL_PROGRAM_TYPE,
  PRIVATE_OFFICES_SUBCATEGORY,
  CUSTOM_CATEGORY,
  MEETING_SPACES_CATEGORY,
  WORKSTATIONS_SUBCATEGORY,
  CUSTOM_SUBCATEGORY,
  GROWTH_CALCULATORS,
  MAX_SEATCOUNT,
  SPACE_NAME_MAP,
} from 'spw-constants';
import {
  IFloor,
  IProjectColors,
  IProjectSpaceTypeCategory,
} from 'spw-redux/modules/projects/types';
import {
  Category,
  ISpaceType,
  ISpaceTypeCategory,
  Subcategory,
} from 'spw-redux/modules/space_types/types';
import { IUser } from 'spw-redux/modules/user/types';
import { IPhoto } from 'spw-redux/modules/photo/types';
import {
  BUILDING_HEADCOUNT_SEATCOUNT,
  BUILDING_USF,
  ITotalProgram,
  Scenario,
} from 'spw-redux/modules/calculators/types';
import { getOs, WINDOWS } from './browser';
import { PlainProgramScenario } from '../models/Scenario';
import { Item } from '../types';
import WorksheetRow from '../models/WorksheetRow';
import { getAlternativeWorkpointParams } from '../models/utils';

type snakeCaseType = typeof _.snakeCase;
type camelCaseType = typeof _.camelCase;

export const BYTES_PER_MEGABYTE = 1024 * 1024;

export const isTeamAdmin = (user: IUser) => user?.adminTeams?.length > 0;

export const getLastEditorName = (lastEditor?: IUser) =>
  lastEditor?.isSuperAdmin ? 'Admin' : lastEditor?.name ?? NO_DATA_TO_SHOW;

export const formatCityState = (
  city: string | null,
  state: string | null,
  noData = NO_DATA_TO_SHOW
) =>
  city || state
    ? `${city || ''}${city && state ? ', ' : ''}${state || ''}`
    : noData;

export function toFixedWithRounding(num: number, precision: number) {
  return num
    ?.toLocaleString('en-US', {
      minimumFractionDigits: precision,
      maximumFractionDigits: precision,
    })
    ?.replace(/,/g, '');
}

export const numberWithCommas = (
  number: any,
  showDecimals?: boolean,
  returnZeroOnNull?: boolean
) => {
  if (!number && !returnZeroOnNull) return null;
  if (!number || !_.isFinite(Number(number)))
    return showDecimals ? '0.00' : '0';
  const newNumber = showDecimals
    ? toFixedWithRounding(Number(number), 2)
    : Math.round(Number(number));
  return newNumber.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

export const fixedNumberWithCommas = (number: number, fixed = 2) => {
  return number.toFixed(fixed).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

function transformWith(transformFn: snakeCaseType | camelCaseType) {
  function transform(o: any): any {
    if (_.isArray(o)) {
      return _.map(o, transform);
    }
    if (_.isPlainObject(o) && !_.isString(o) && !_.isNumber(o)) {
      return _(o)
        .mapKeys((value, key: any) => transformFn(key))
        .mapValues((value) => transform(value))
        .value();
    }
    return o;
  }

  return (o: any) => transform(o);
}

export const camelize = (o: any) => transformWith(_.camelCase)(o);
export const snakeize = (o: any) => transformWith(_.snakeCase)(o);

export const encodeObjectsArray = (list: any[]) =>
  JSON.stringify(list).replace(/"(\w+)"\s*:/g, '$1:');

export const removeTypeNameFromObject = (object: any) => {
  if (!object) return object;
  // eslint-disable-next-line no-param-reassign
  if ('__typename' in object) delete object.__typename;
  // eslint-disable-next-line no-param-reassign
  if ('typename' in object) delete object.typename;
  return object;
};

export const sumProp = (key: string) =>
  R.pipe(R.map(R.prop(key)) as (a: any[]) => number[], R.sum);

export const sumUSF = (a: any[]) => +sumProp('usf')(a).toFixed(2);

export const sumSeatCount = sumProp('seatCount');

export const sumAltWorkpointSeats = (spaces: ISpace[]) =>
  spaces.reduce(
    (acc, s) =>
      acc + getAlternativeWorkpointParams(s).alternativeWorkpointSeats,
    0
  );

export const base64ToBlob = (b64: string) =>
  fetch(b64)
    .then((res) => res.blob())
    .catch((error) => {
      Sentry.captureException(error);
      return Promise.resolve('');
    });

export const textOrDash = (value: any, dash = '--') =>
  R.ifElse(
    (v) => isNaN(v) || R.isNil(v) || R.isEmpty(v) || !isFinite(v) || v === 0,
    R.always(dash),
    R.identity
  )(value);

export const numberOrDash = (value: number, dash = '--') =>
  R.ifElse(
    (v) => isNaN(v) || R.isNil(v) || R.isEmpty(v) || !isFinite(v),
    R.always(dash),
    R.identity
  )(value);

export const valueOrZero = R.ifElse(
  (v) => isNaN(v) || R.isNil(v) || R.isEmpty(v) || !isFinite(v) || v === 0,
  R.always('0'),
  R.identity
);

export const removeBrackets = (text: string) => text.replace(/\(.*\)/g, '');

export const preappendZero = (n: number) => {
  return n > 9 ? ['', n].join('') : ['0', n].join('');
};

export const makeDownloadFilenameSafe = (str: string) =>
  _.trimStart(str, '-. ') // remove invalid chars from start of name
    .replace(/[^\w\s/._-]/gi, '') // https://superuser.com/a/748264
    .replace(/\s{2,}/, ' '); // only allow a single whitespace in a row

export const getZeroWithDecimals = (numberOfDecimals: number): string => {
  const decimalZeros = '0'.repeat(numberOfDecimals);

  return `0${numberOfDecimals ? '.' : ''}${decimalZeros}`;
};

export const asPercentage = (quantity: number, numberOfDecimals = 2) => {
  if (_.isNil(quantity)) return quantity;
  return R.pipe(
    R.multiply(100),
    (value: number) =>
      numberOfDecimals === 0
        ? Math.round(value)
        : value.toFixed(numberOfDecimals),
    (value) => `${value || getZeroWithDecimals(numberOfDecimals)}%`
  )(quantity);
};

export const asPercentageRounded = (quantity: number, showNoData?: boolean) => {
  if (_.isNaN(Math.round(quantity)))
    return showNoData ? `${NO_DATA_TO_SHOW}%` : `0%`;
  if (!_.isFinite(Math.round(quantity)))
    return showNoData ? `${NO_DATA_TO_SHOW}%` : `0%`;
  if (!quantity) return showNoData ? `${NO_DATA_TO_SHOW}%` : `${quantity}%`;
  return R.pipe(
    R.multiply(100),
    (value: number) => Math.round(value),
    (value) => numberWithCommas(value) || 0,
    (value) => `${value}%`
  )(quantity);
};

export const asPercentageFixed = (quantity: number, showNoData?: boolean) => {
  if (_.isNaN(Math.round(quantity)))
    return showNoData ? `${NO_DATA_TO_SHOW}%` : `0.00%`;
  if (!_.isFinite(Math.round(quantity)))
    return showNoData ? `${NO_DATA_TO_SHOW}%` : `0.00%`;
  if (!quantity) return showNoData ? `${NO_DATA_TO_SHOW}%` : `${quantity}.00%`;
  return R.pipe(
    R.multiply(100),
    (value) => numberWithCommas(value, true, true) || 0,
    (value) => `${value}%`
  )(quantity);
};

export const prohibitedUsfInputChars = (allowDecimals: boolean) => {
  if (allowDecimals) {
    return ['-', '+', 'e'];
  }
  return ['-', '+', '.', 'e'];
};

export const parseNumberIntl = (
  value: string,
  locales = navigator.languages
) => {
  const example = Intl.NumberFormat(locales as string[]).format(1.1);
  const cleanPattern = new RegExp(`[^-+0-9${example.charAt(1)}]`, 'g');
  const cleaned = value.replace(cleanPattern, '');
  const normalized = cleaned.replace(example.charAt(1), '.');

  return parseFloat(normalized);
};

export const asRatio = (quantity: number, fixed = 2, left = false) => {
  if (!quantity) return quantity;

  return R.pipe(
    (value: number) => value.toFixed(fixed),
    (value: string) => (left ? `${value}:1` : `1:${value}`)
  )(quantity);
};

export const commaSeparated = (toSeparate: number) => {
  if (typeof toSeparate !== 'number') {
    return toSeparate;
  }

  const isNegative = toSeparate < 0;
  const num = `${toSeparate}`.replace(/-/, '');
  const stack = [];

  try {
    for (let i = 0; i < num.length; i += 1) {
      if (i % 3 === 0 && i !== 0) {
        stack.push(',');
      }

      stack.push(num[num.length - i - 1]);
    }

    stack.reverse();

    return isNegative ? `-${stack.join('')}` : stack.join('');
  } catch (err) {
    return num;
  }
};

export const formatBigNumber = (v: unknown) => {
  return v.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

export const isNotZero = (value: number) => value !== 0;

export const isNotNaN = (value: number) => !_.isNaN(value);

export const asUsf = (val: number, showDecimals?: boolean) => {
  if (isFinite(val) && isNotZero(val) && isNotNaN(val)) {
    if (!showDecimals) {
      return R.pipe(Math.round, formatBigNumber)(val);
    }
    return formatBigNumber(val.toFixed(2));
  }
  return textOrDash(val);
};

export const asUsfOrZero = (
  val: number,
  showDecimals?: boolean,
  decimals = 2
) => {
  if (isFinite(val) && isNotNaN(val)) {
    if (!showDecimals) {
      return R.pipe(Math.round, formatBigNumber)(val);
    }
    return formatBigNumber(toFixedWithRounding(val, decimals));
  }
  return showDecimals ? `0.${'0'.repeat(decimals)}` : '0';
};

type ICapitalizeData = Array<Array<number | string>>;
export const capitalizeData = (data: ICapitalizeData) =>
  data.map((row: Array<number | string>) =>
    row.map((e) => (typeof e === 'string' ? e.toUpperCase() : e))
  );

export const validateUsf = (number: string, max: number) => {
  const nextNumber = Number.parseFloat(number);

  if (nextNumber > max) return max;

  if (nextNumber >= 0) {
    return nextNumber;
  }
  return 0;
};

export const validateSeatCount = (number: string, lowest: number) => {
  const nextNumber = Number.parseInt(number || '0', 10);

  if (nextNumber > MAX_SEATCOUNT) return MAX_SEATCOUNT;

  if (nextNumber >= lowest) {
    return nextNumber;
  }
  return lowest;
};

export const getBrowserInfo = () => {
  const { browser, os } = new UAParser().getResult();
  return `${os?.name} ${os?.version} - ${browser?.name} ${browser?.version}`;
};

/* This function return a category given a space type id */
export const categoryBySpaceTypeId = (
  spaceTypeId: number,
  categories: SpaceTypeCategoryMap
) =>
  R.find(
    R.pipe(
      R.prop(R.__, categories) as (c: Category) => ISpaceTypeCategory,
      R.prop('spaceTypes') as (c: ISpaceTypeCategory) => ISpaceType[],
      R.find(R.propEq('id', spaceTypeId)) as (
        s: ISpaceType[]
      ) => ISpaceType | undefined,
      R.isNil,
      R.not
    ),
    R.keys(categories)
  );

const usageToReadableMap = {
  AWAY_FROM_DESK: 'Away from desk',
  MEET_WITH_OTHERS: 'Meet with others at desk',
  ON_THE_PHONE: 'On the phone',
  OOO_VACANT: 'OOO or vacant desk',
  QUIET_FOCUSED_LITTLE_INTERACTION: 'Quiet, focused, little interaction',
  WORK_AREA_TO_CALIBRATE: 'Gather by work area to collaborate',
};

export const roomNumberLabel = (spaceName: string) => {
  if (spaceName === 'Hotel Desk' || spaceName === 'Workstation') {
    return `${spaceName} Number`;
  }
  if (spaceName === 'Custom Workstation') {
    return `Workstation Number`;
  }

  return `Room Number`;
};

export const roomNameLabel = (spaceName: string) => {
  if (spaceName === 'Custom Workstation') {
    return `Workstation Name`;
  }
  if (spaceName === 'Custom Office') {
    return `Private Office Name`;
  }

  return `${spaceName} Name`;
};

export const usageToText = (usage: IUsage) => usageToReadableMap[usage];

export const removeParentesisAndContent = (spaceName: string) =>
  spaceName ? spaceName.replace(/ *\([^)]*\) */g, '') : '';

const isLatLngList = (
  x: LatLngRing | LatLngPolygon | LatLngMultiPolygon
): x is LatLngRing => {
  if (x && !isNil(x.length)) {
    if (x[0] instanceof LatLng) {
      return true;
    }
  }
  return false;
};

const isPolygon = (
  x: LatLngRing | LatLngPolygon | LatLngMultiPolygon
): x is LatLngPolygon => {
  if (x?.[0]) {
    if (x[0] instanceof LatLng) {
      return false;
    }
    if (x[0]?.[0] instanceof LatLng) return true;
    if (x[0]?.[0]?.[0] instanceof LatLng) return false;
  }
  return false;
};

const isMultiPolygon = (
  x: LatLngRing | LatLngPolygon | LatLngMultiPolygon
): x is LatLngMultiPolygon => {
  if (x?.[0]) {
    if (x[0] instanceof LatLng) {
      return false;
    }
    if (x[0]?.[0] instanceof LatLng) return false;
    if (x[0]?.[0]?.[0] instanceof LatLng) return true;
  }
  return false;
};

const isLatLngMatrix = (
  x: LatLngRing | LatLngPolygon | LatLngMultiPolygon
): x is LatLngPolygon | LatLngMultiPolygon => isPolygon(x) || isMultiPolygon(x);

const isSpace = (x: any): x is ISpace => {
  if (x.number && (x.number > 1 || x.isCluster)) {
    return false;
  }
  return true;
};

export const isWorksheetRow = (row: any): row is WorksheetRow =>
  typeof row.category === 'string';

const isScenario = (x: Scenario | ITotalProgram): x is Scenario => {
  if (x.calculatorType !== TOTAL_PROGRAM_TYPE) {
    return true;
  }
  return false;
};

export const typeCheck = {
  isLatLngList,
  isLatLngMatrix,
  isPolygon,
  isMultiPolygon,
  isSpace,
  isWorksheetRow,
  isScenario,
};

export const asRatioRoundNumber = (quantity: number, left = false) => {
  if (!quantity) return quantity;
  return R.pipe(
    (value: number) => Math.round(value),
    (value) => (left ? `${value}: 1` : `1: ${value}`)
  )(quantity);
};

export const isOtherSpace = (space: ISpace) =>
  _.camelCase(space?.spaceType?.spaceTypeCategory?.category) === OTHER_CATEGORY;

export const formatSpaceName = (
  space: ISpace,
  { clientFacingNamesAreVisible }: { clientFacingNamesAreVisible?: boolean }
) => {
  const selectedSpaceTypeName =
    clientFacingNamesAreVisible && space.projectSpaceType?.clientFacingName
      ? space.projectSpaceType?.clientFacingName
      : space.spaceType.name;
  return removeParentesisAndContent(selectedSpaceTypeName);
};

export const formatNameDownload = (
  floor: IFloor,
  space: ISpace,
  index: number,
  format: string
) => {
  const floorName = floor.name?.replace(' ', '').toUpperCase();

  const type = isOtherSpace(space) ? space.roomName : space.spaceType.name;

  const typeName = isNil(type)
    ? type
    : R.pipe(removeParentesisAndContent, _.snakeCase, _.toUpper)(type);
  const roomName =
    space.roomNumber && space.roomNumber !== 'null'
      ? `RM${space.roomNumber}`
      : '';
  const photoIndex = `${(index + 1).toString().padStart(2, '0')}`;
  const fileName = [floorName, typeName, roomName, photoIndex]
    .filter((name) => name)
    .join('_');

  return `${fileName}.${format}`;
};

export const formatNoteIndex = (index: number) => `${index}`.padStart(2, '0');

export const formatNameDisplay = (
  space: ISpace,
  index: number,
  { clientFacingNamesAreVisible } = { clientFacingNamesAreVisible: false }
) => {
  const { roomNumber, roomName } = space;
  const name =
    clientFacingNamesAreVisible && space.projectSpaceType?.clientFacingName
      ? space.projectSpaceType?.clientFacingName
      : space.spaceType.name;
  const formattedSpaceName = removeParentesisAndContent(name);
  const formattedRoomNumber = roomNumber ? `#${roomNumber}` : '';
  const formattedIndex = formatNoteIndex(index);
  if (
    camelCase(space?.spaceType?.spaceTypeCategory?.category) === OTHER_CATEGORY
  ) {
    return `${roomName} - ${formattedIndex}`;
  }
  return `${formattedSpaceName} ${formattedRoomNumber} - ${formattedIndex}`;
};

export const packPolygonsMatrixForPersistence = (polygons: LatLng[][][]) => {
  const normalizedPoints: { x: number; y: number }[][][] = [];
  _.each(polygons, (polygon: LatLng[][]) => {
    const normalizedPolygon = _.map(polygon, (pol: LatLng[]) =>
      pol.map((p) => ({
        x: p.lat,
        y: p.lng,
      }))
    ); // map old format to input_point format
    normalizedPoints.push(normalizedPolygon);
  });

  return encodeObjectsArray(normalizedPoints);
};

export const unPackPolygonsMatrixForPersistence = (
  normalizedPolygons: { x: number; y: number }[][][]
) =>
  normalizedPolygons.map((polygon: { x: number; y: number }[][]) =>
    _(polygon)
      .map((ring: { x: number; y: number }[]) =>
        ring.map(({ x, y }: { x: number; y: number }) => new LatLng(x, y))
      )
      .value()
  );

export const parseAsLocalDate = (
  date: string | moment.Moment,
  format = 'MM/DD/YYYY h:mm a'
) => moment.utc(date).local().format(format);

export const spaceName = (space: ISpace) => {
  if (space?.spaceType?.name) {
    const roomNumber = space.roomNumber ? `#${space.roomNumber}` : '';
    return `${removeBrackets(space.spaceType.name)} ${roomNumber}`;
  }
  return 'No Name';
};

export const numberWithCommasOrDash = (
  amount: number,
  showDecimals?: boolean
) => {
  if (!isFinite(amount)) {
    return '--';
  }
  return asUsf(amount, showDecimals);
};

export const camelCapitalize = (value: string) => capitalize(camelCase(value));

export const compareStrings = (stringA: string, stringB: string) =>
  stringA?.toLowerCase()?.trim() === stringB?.toLowerCase()?.trim();

export const toFixed = (num: number) => {
  return num ? +num.toFixed(2) : '--';
};

const compareStringsForSorting = (a: string, b: string) => {
  if (a >= b) return 1;
  if (a <= b) return -1;
  return 0;
};

export const sortSuggestions = <T>(
  suggestions: T[],
  iteratees: (item: T) => string,
  searchText = ''
) => {
  return suggestions.sort((a, b) => {
    const aComparingString = iteratees(a).toLowerCase();
    const bComparingString = iteratees(b).toLowerCase();
    const search = searchText.toLowerCase();

    if (
      aComparingString.startsWith(search) &&
      bComparingString.startsWith(search)
    )
      return compareStringsForSorting(aComparingString, bComparingString);
    if (
      aComparingString.startsWith(search) &&
      !bComparingString.startsWith(search)
    )
      return -1;
    if (
      !aComparingString.startsWith(search) &&
      bComparingString.startsWith(search)
    )
      return 1;
    return compareStringsForSorting(aComparingString, bComparingString);
  });
};

export const reorder = (list: any[], startIndex: number, endIndex: number) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return _.map(result, (r: any, i: number) => ({ ...r, index: i }));
};

export const composePhotoLink = (photo: IPhoto) => {
  const {
    location: { protocol, host },
  } = window;
  const root = `${protocol}//${host}`;
  return `${root}/image/${photo.id}`;
};

export const getAriaLabelForSensitiveInfo = ({
  context,
  desc,
  id,
}: {
  context: string;
  desc: string;
  id: number | string;
}) => {
  return `(${context}) ${desc} [${id}]`;
};

export const sumUsf = (spaceTypes: ISpaceBreakDown[], spaceType: string) =>
  spaceTypes
    ?.filter(
      (spaceTypeBreakdown: ISpaceBreakDown) =>
        spaceTypeBreakdown.spaceType === spaceType
    )
    ?.reduce((sum, spaceTypeBreakdown: ISpaceBreakDown) => {
      return sum + spaceTypeBreakdown.usf;
    }, 0);

export const handleOnKeyPress = (
  event: React.KeyboardEvent<HTMLInputElement>
) => {
  if (event.key === 'Enter') {
    (event.target as HTMLInputElement).blur();
  }
};

export const getFormattedTime = (time: Duration) => {
  return `${time.minutes()}:${_.padStart(time.seconds().toString(), 2, '0')}`;
};

export type Keys = Category | Subcategory | 'sharedSpaces';

export const getColor = (
  spaceKey: Keys,
  projectColors: IProjectColors
): string => {
  let colorSwatch = projectColors;
  const safeSpaceKey = camelCase(spaceKey);

  if (!projectColors) colorSwatch = DEFAULT_COLORS;

  let color = colorSwatch[safeSpaceKey];

  if (
    safeSpaceKey === WORKSTATIONS_SUBCATEGORY ||
    safeSpaceKey === PRIVATE_OFFICES_SUBCATEGORY ||
    safeSpaceKey === CUSTOM_SUBCATEGORY
  ) {
    color = colorSwatch[INDIVIDUAL_WORKSPACES_CATEGORY];
  }
  if (!color && safeSpaceKey !== CUSTOM_CATEGORY) {
    color = DEFAULT_COLORS[safeSpaceKey];
  }
  if (!color) {
    color = '#31708f';
  }
  return color as string;
};

export const getCustomSpaceColor = (
  category: Category | Subcategory,
  spaceType: string,
  projectColors: IProjectColors
) => {
  const defaultColor = projectColors[category];
  switch (category) {
    case WORKSTATIONS_SUBCATEGORY:
    case PRIVATE_OFFICES_SUBCATEGORY:
    case INDIVIDUAL_WORKSPACES_CATEGORY: {
      const isOffice = spaceType.includes('office');
      const colorArray = projectColors[CUSTOM_WORKSPACE_COLORS];
      return isOffice ? colorArray[2] : colorArray[3] || defaultColor;
    }
    case CUSTOM_SUBCATEGORY:
      return (
        projectColors[CUSTOM_WORKSPACE_COLORS][7] ||
        projectColors[INDIVIDUAL_WORKSPACES_CATEGORY]
      );
    case MEETING_SPACES_CATEGORY: {
      const colorArray = projectColors[CUSTOM_MEETING_SPACE_COLORS];
      return colorArray[5] || defaultColor;
    }
    default:
      return defaultColor;
  }
};

export const isDepartmentsTypeScenario = (
  scenario: Scenario | ITotalProgram | PlainProgramScenario
) =>
  scenario?.calculatorType ===
    GROWTH_CALCULATORS.buildingHeadcountSeatcountDepartments ||
  scenario?.calculatorType === TOTAL_PROGRAM_TYPE;

export const limitToCharLength = (value: string, limiter: number) =>
  value?.slice(0, String(limiter).length);

export const insertAtIndex = <T>(arr: T[], index: number, newItem: T) => {
  return [...arr.slice(0, index), newItem, ...arr.slice(index)];
};

export const replaceAtIndex = <T>(arr: T[], item: T, index: number) => {
  return [...arr.slice(0, index), item, ...arr.slice(index + 1)];
};

export const deleteAtIndex = <T>(arr: T[], index: number) => {
  return [...arr.slice(0, index), ...arr.slice(index + 1)];
};

export const removeNonNumericCharacters = (value: string) =>
  value.replace(/[^0-9]/g, '');

export const getZipNamePrefix = (
  scenario: Scenario | PlainProgramScenario,
  prefix = ''
) => {
  let zipName = '';
  if (isDepartmentsTypeScenario(scenario)) {
    const { department } = scenario;
    zipName += department ? `${department.name}_` : 'Multiple Depts_';
  } else if (
    scenario.calculatorType === BUILDING_HEADCOUNT_SEATCOUNT ||
    scenario.calculatorType === BUILDING_USF
  ) {
    zipName += `Bldg_`;
  } else {
    zipName += `${prefix}_`;
  }

  return zipName;
};

export const isWindows = () => getOs() === WINDOWS;

/*
  The function below operates as a last line of defense to try and retrieve something from an error
  It shouldn't be used in any context unless *something* is better than nothing, as there is little control
  over what gets deleted
*/

export const reduceObjectSizeToLimit = (
  object: Record<string, unknown>,
  limit: number
): Record<string, unknown> => {
  const reduced = object;
  try {
    // then start removing values
    if (JSON.stringify(reduced)?.length > limit) {
      // find the key containing the largest value in the object
      const largestKey = _.reduce(
        reduced,
        (l: string, _curr: Record<string, unknown>, key: string) => {
          return l &&
            (typeof reduced[key] !== 'object' ||
              JSON.stringify(reduced[l]).length >
                JSON.stringify(reduced[key]).length)
            ? l
            : key;
        },
        null
      );
      if (largestKey === 'breadcrumbs') {
        reduced.breadcrumbs = (reduced.breadcrumbs as unknown[])?.filter(
          (bc: any) => bc.category !== 'redux.action'
        );
      } else {
        delete reduced[largestKey];
      }
      // delete it
      if (JSON.stringify(reduced).length > limit) {
        // if it still doesn't pass the test, re-iterate
        return reduceObjectSizeToLimit(reduced, limit);
      }
    }
    return reduced;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.log('Sentry exception', e);
    // if something bad happens, hail mary the best try over the finish line
    return reduced;
  }
};

export const getWorkSeatsMix = (
  targetWorkSeats: number,
  assignedWorkSeats: number,
  options?: {
    showDecimals: boolean;
    showNoData?: boolean;
  }
) => {
  const showNoData = !isNil(options.showNoData) ? options.showNoData : true;

  if (!_.isNaN(assignedWorkSeats / targetWorkSeats)) {
    return options?.showDecimals
      ? asPercentageFixed(assignedWorkSeats / targetWorkSeats, showNoData)
      : asPercentageRounded(assignedWorkSeats / targetWorkSeats, showNoData);
  }
  return null;
};

export const isSeatsAssignmentComplete = (
  targetWorkSeats: number,
  assignedWorkSeats: number
): boolean => {
  const completedSeatAllocationThreshold = 0.5;
  return (
    Math.abs(targetWorkSeats - assignedWorkSeats) <
    completedSeatAllocationThreshold
  );
};

export const hexToFilter = (hex: string) => {
  return `brightness(0) saturate(100%) ${hexToCSSFilter(hex).filter.replace(
    ';',
    ''
  )}`;
};

export const getProjectSpaceTypeCategoryByCategory = (
  spaceTypeCategories: IProjectSpaceTypeCategory[],
  category: Category | Subcategory
): IProjectSpaceTypeCategory => {
  return spaceTypeCategories.find(
    (spaceTypeCategory) =>
      spaceTypeCategory.defaultSpaceTypeCategory.category === category ||
      spaceTypeCategory.subcategories.find(
        (subcategory) =>
          subcategory.defaultSpaceTypeCategory.category === category
      )
  );
};
export const applyOverflowingRulesToDefaultPosition = (
  getDefaultPosition: (rect: DOMRect) => { x: number; y: number },
  element: HTMLElement,
  wrapperEl?: HTMLElement,
  horizontalProp: 'left' | 'right' = 'left'
) => {
  const rect = element.getClientRects()[0];

  const defaultPosition = getDefaultPosition(rect);

  // If the dialog would appear partially out of screen to the top
  if (rect.top < 0 || defaultPosition.y < 0) {
    defaultPosition.y = 0;

    return {
      top: defaultPosition.y,
      [horizontalProp]: defaultPosition.x,
    };
  }

  if (wrapperEl) {
    // If the dialog would appear partially out of screen to the bottom
    if (rect.top + wrapperEl.clientHeight > window.innerHeight) {
      const surplus = rect.top + wrapperEl.clientHeight - window.innerHeight;
      defaultPosition.y = rect.top - surplus;
    }
  }

  return {
    top: defaultPosition.y,
    [horizontalProp]: defaultPosition.x,
  };
};

export const sanitizeFileName = (fileName: string) => {
  const [_regex, name, ext] = fileName.match(/^(.*?)(\.zip)?$/);
  return `${name.replace(/[.\\/:"*?<>|]/g, '-')}${ext || ''}`;
};

export const removeLineBreaks = (text?: string) => {
  return text?.replace(/ \n|\n /g, ' ').replace(/\n/g, ' ');
};

// use with css rule `white-space: pre-line` to render line breaks in html
export const breakLineAtChar = (
  line: string,
  char: string,
  maxCharLength: number
) => {
  if (line.length < maxCharLength) return line;
  const [first, ...restArray] = line.split(char);
  let rest = restArray.join(char);
  if (rest.length > maxCharLength) {
    rest = breakLineAtChar(rest, char, maxCharLength);
  }
  return [first, rest].join(`${char}\n`);
};

export const getProposedCloneName = (
  originalName: string,
  existingNames: string[]
) => {
  const matchResult = originalName.match(/^(.*)?\s(\d+)$/);

  let baseName: string;
  let copyIteration: string;
  if (!matchResult) {
    baseName = originalName;
    copyIteration = '1';
  } else {
    [, baseName, copyIteration] = matchResult;
  }

  const existingNamesLowerCase = existingNames?.map((n) => n.toLowerCase());

  const nameProposal = `${baseName} ${(parseInt(copyIteration, 10) || 1) + 1}`;
  if (existingNamesLowerCase.includes(nameProposal.toLowerCase())) {
    const nameSpecificRegex = new RegExp(
      `^${baseName
        .toLowerCase()
        .replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&')}(\\s*(\\d+)?)?$`
    );
    const newIteration = existingNamesLowerCase.reduce(
      (copyNumber, scenarioName) => {
        const match = scenarioName.match(nameSpecificRegex);
        if (match === null) {
          return copyNumber;
        }
        const innerCopyIteration = match[2];
        const copyIterationNumber = parseInt(innerCopyIteration, 10);
        return copyIterationNumber && copyIterationNumber > copyNumber
          ? copyIterationNumber
          : copyNumber;
      },
      parseInt(copyIteration, 10) || 1
    );
    return `${baseName} ${newIteration + 1}`;
  }
  return nameProposal;
};

export function getIdentity(item: Item | string) {
  return isString(item) ? item : String(item.id);
}

export const calibrationChanged = (
  calibration: ICalibration,
  originalCalibration: ICalibration
) => {
  if (!calibration || !originalCalibration) {
    return false;
  }

  const properties = ['x1', 'x2', 'y1', 'y2'] as (keyof ICalibration)[];

  return properties.some((p) => originalCalibration[p] !== calibration[p]);
};

export const getWidthForString = (
  str: string,
  cssProperties: Partial<CSSStyleDeclaration>
) => {
  const el = document.createElement('div');

  const properties = {
    ...cssProperties,
    position: 'absolute',
    top: '0',
    left: '0',
  } as CSSStyleDeclaration;

  for (const key in properties) {
    if (Object.prototype.hasOwnProperty.call(properties, key)) {
      el.style[key] = properties[key];
    }
  }

  el.innerHTML = str;

  document.body.appendChild(el);

  const { width } = el.getBoundingClientRect();

  document.body.removeChild(el);

  return width;
};

export const NON_WORKSPACE_CATEGORIES = [
  'meetingSpaces',
  'amenitySpaces',
  'supportSpaces',
];

export const getIsIndividualWorkspace = (spaceType: string) =>
  [
    SPACE_NAME_MAP.INDIVIDUAL_WORKSPACES.WORKSTATION,
    SPACE_NAME_MAP.INDIVIDUAL_WORKSPACES.HOTEL_DESK,
    SPACE_NAME_MAP.PRIVATE_OFFICES.PRIVATE_OFFICE,
    SPACE_NAME_MAP.PRIVATE_OFFICES.SHARED_PRIVATE_OFFICE,
    SPACE_NAME_MAP.PRIVATE_OFFICES.EXECUTIVE_PRIVATE_OFFICE,
  ].includes(spaceType);

export const getIsLockerOrCabinet = (spaceType: string) =>
  [
    SPACE_NAME_MAP.SUPPORT_SPACES.FILE_CABINET,
    SPACE_NAME_MAP.SUPPORT_SPACES.STORAGE_LOCKER,
  ].includes(spaceType);

export const getIsFileCabinet = (spaceType: string) =>
  spaceType === SPACE_NAME_MAP.SUPPORT_SPACES.FILE_CABINET;

export const getIsLocker = (spaceType: string) =>
  spaceType === SPACE_NAME_MAP.SUPPORT_SPACES.STORAGE_LOCKER;

export const isBackdropOpenOnFloorplanPage = () => {
  if (
    document.getElementById('spw-popper-backdrop')?.style?.visibility !==
    'hidden'
  )
    return true;

  if (document.querySelector('.MuiDialog-root[role="presentation"]'))
    return true;

  if (document.querySelector('.MuiPopover-root[role="presentation"]'))
    return true;

  return false;
};

export const createInvisiblePixelAtClickEventCoordinates = (e: MouseEvent) => {
  const pixel = document.createElement('div');

  pixel.style.position = 'fixed';
  pixel.style.top = `${e.y}px`;
  pixel.style.left = `${e.x}px`;

  return pixel;
};

type RoundedPercentage = {
  id?: string;
  percentage: number;
  roundingDirection: 'up' | 'down';
  valueIsGreaterThanZeroButLessThanOne: boolean;
  hasCorrectionApplied: boolean;
};

export const getBalancedRoundedPercentages = <T>(
  values: ({ percentage: number; id?: string } & T)[]
): (Omit<RoundedPercentage, 'roundingDirection'> & T)[] => {
  const totalPercentage = values.reduce(
    (acc, value) => acc + Math.round(value.percentage),
    0
  );
  const roundedValues: (RoundedPercentage & T)[] = values.map((value) => {
    const roundedPercentage = Math.round(value.percentage);
    const roundingDirection =
      roundedPercentage - Math.floor(value.percentage) === 1 ? 'up' : 'down';

    return {
      hasCorrectionApplied: false,
      valueIsGreaterThanZeroButLessThanOne:
        value.percentage < 1 && value.percentage > 0,
      ...value,
      percentage: roundedPercentage,
      roundingDirection,
    };
  });
  const totalPercentageDiff = 100 - totalPercentage;
  if (
    totalPercentageDiff !== 0 &&
    Math.abs(totalPercentageDiff) <= values.length
  ) {
    let correctionHasBeenApplied = false;
    const balancedRoundedPercentages = roundedValues.map((roundedValue) => {
      const correctionValue = totalPercentageDiff < 0 ? -1 : 1;
      const directionTarget = totalPercentageDiff < 0 ? 'up' : 'down';
      if (
        correctionHasBeenApplied ||
        directionTarget !== roundedValue.roundingDirection
      )
        return roundedValue;

      correctionHasBeenApplied = true;
      return {
        ...roundedValue,
        percentage: roundedValue.percentage + correctionValue,
        hasCorrectionApplied: true,
      };
    });
    return getBalancedRoundedPercentages(balancedRoundedPercentages);
  }
  return roundedValues.map(
    (r) =>
      omit(r, 'roundingDirection') as Omit<
        RoundedPercentage,
        'roundingDirection'
      > &
        T
  );
};

const MIME_TYPES: Record<string, string> = {
  png: 'image/png',
  jpg: 'image/jpeg',
  pdf: 'application/pdf',
  zip: 'application/zip',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
};

export const convertBase64StringToBlob = (
  base64String: string,
  format: string
) => {
  const byteChars = atob(base64String);
  const arrayBuffer = new Array(byteChars.length);

  for (let i = 0; i < byteChars.length; i++) {
    arrayBuffer[i] = byteChars.charCodeAt(i);
  }
  const byteArray = new Uint8Array(arrayBuffer);

  const blob = new Blob([byteArray], {
    type: MIME_TYPES[format],
  });
  return blob;
};

export const allowOnlyNumericInput: React.KeyboardEventHandler = (event) => {
  if (
    !/^\d*\.?\d*$/.test(event.key) &&
    event.key?.toLowerCase() !== 'backspace'
  ) {
    event.preventDefault();
  }
};

export const buildRankMap = (items: string[]) => {
  return items.reduce(
    (rankMap, item, index) => ({
      ...rankMap,
      [item]: index,
    }),
    {}
  );
};
